import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell, AreaChart, Area } from 'recharts';
import { Gauge, Loader2, RefreshCw, Dices } from 'lucide-react';
import { analysesApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-800 p-3 rounded-lg shadow-xl max-w-xs">
      <p className="text-xs text-zinc-200 font-medium mb-2">{d.variable}</p>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between gap-4">
          <span className="text-red-400">Low ({d.lowLabel}):</span>
          <span className="font-mono text-zinc-200">{fmt(d.ev_low)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-zinc-400">Base:</span>
          <span className="font-mono text-zinc-200">{fmt(d.ev_base)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-emerald-400">High ({d.highLabel}):</span>
          <span className="font-mono text-zinc-200">{fmt(d.ev_high)}</span>
        </div>
        <div className="border-t border-zinc-700 pt-1 mt-1 flex justify-between gap-4">
          <span className="text-indigo-400">Spread:</span>
          <span className="font-mono font-medium text-indigo-300">{fmt(d.spread)}</span>
        </div>
      </div>
    </div>
  );
};

export default function SensitivityPage({ analysis, results, data: analysisData, saveAnalysis, onRefresh }) {
  // Load from saved results if available
  const savedSensitivity = results?.sensitivity || null;
  const savedMonteCarlo = results?.monte_carlo || null;
  
  const [data, setData] = useState(savedSensitivity);
  const [loading, setLoading] = useState(false);
  const [swingPct, setSwingPct] = useState(0.20);
  const [mcData, setMcData] = useState(savedMonteCarlo);
  const [mcLoading, setMcLoading] = useState(false);
  const [nSims, setNSims] = useState(1000);

  // Load saved data when analysis changes
  useEffect(() => {
    if (results?.sensitivity) {
      setData(results.sensitivity);
    }
    if (results?.monte_carlo) {
      setMcData(results.monte_carlo);
    }
  }, [results?.sensitivity, results?.monte_carlo]);

  const runSensitivity = useCallback(async () => {
    if (!analysis?.id) return;
    setLoading(true);
    try {
      const res = await analysesApi.sensitivity(analysis.id, swingPct);
      setData(res.data);
      toast.success('Sensitivity analysis complete & saved');
      // Refresh to get updated results in parent
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Sensitivity error:', e);
      toast.error('Failed to run sensitivity analysis');
    } finally {
      setLoading(false);
    }
  }, [analysis, swingPct, onRefresh]);

  const runMonteCarlo = useCallback(async () => {
    if (!analysis?.id) return;
    setMcLoading(true);
    try {
      const res = await analysesApi.monteCarlo(analysis.id, nSims);
      setMcData(res.data);
      toast.success('Monte Carlo simulation complete & saved');
      // Refresh to get updated results in parent
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error('Monte Carlo error:', e);
      toast.error('Failed to run Monte Carlo');
    } finally {
      setMcLoading(false);
    }
  }, [analysis, nSims]);

  useEffect(() => {
    // Only auto-run if no saved data exists
    if (analysis?.id && analysis?.status === 'calculated' && !savedSensitivity) {
      runSensitivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.id]);

  if (!analysis?.id) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="sensitivity-page">
        Import data to view sensitivity analysis
      </div>
    );
  }

  const formatVarLabel = (val, key) => {
    if (key === 'buyer_salary') return fmt(val);
    return `${(val * 100).toFixed(1)}%`;
  };

  const tornadoData = (data?.variables || []).map(v => ({
    variable: v.variable,
    ev_low: v.ev_low,
    ev_high: v.ev_high,
    ev_base: v.ev_base,
    spread: v.spread,
    lowLabel: formatVarLabel(v.low_value, v.key),
    highLabel: formatVarLabel(v.high_value, v.key),
    // For the tornado chart, we need offset bars from base
    negative: Math.min(v.ev_low, v.ev_high) - v.ev_base,
    positive: Math.max(v.ev_low, v.ev_high) - v.ev_base,
    negativeAbs: Math.min(v.ev_low, v.ev_high),
    positiveAbs: Math.max(v.ev_low, v.ev_high),
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sensitivity-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Sensitivity / Tornado Analysis</h2>
          <p className="text-sm text-zinc-400 mt-1">How enterprise value changes when each assumption varies</p>
        </div>
        <div className="flex items-center gap-3">
          <SectionPdfExport
            title="Sensitivity Analysis"
            analysisName={analysis?.name}
            rows={tornadoData}
            columns={buildColumns([
              { label: 'Variable', key: 'variable' },
              { label: 'EV (Low)', key: 'ev_low', format: 'currency' },
              { label: 'EV (Base)', key: 'ev_base', format: 'currency' },
              { label: 'EV (High)', key: 'ev_high', format: 'currency' },
              { label: 'Spread', key: 'spread', format: 'currency' },
            ])}
            summary={mcData ? {
              title: 'Monte Carlo Summary',
              items: [
                { label: 'Mean EV', value: fmt(mcData.mean) },
                { label: 'Std Dev', value: fmt(mcData.std_dev) },
                { label: 'P10', value: fmt(mcData.percentiles?.p10) },
                { label: 'P90', value: fmt(mcData.percentiles?.p90) },
              ]
            } : undefined}
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400">Swing:</span>
            <span className="font-mono text-sm text-zinc-200">&plusmn;{(swingPct * 100).toFixed(0)}%</span>
          </div>
          <Slider
            value={[swingPct]}
            min={0.05}
            max={0.50}
            step={0.05}
            onValueChange={([v]) => setSwingPct(v)}
            className="w-24"
            data-testid="swing-slider"
          />
          <Button
            onClick={runSensitivity}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="run-sensitivity-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recalculate
          </Button>
        </div>
      </div>

      {/* Base EV */}
      {data && (
        <Card className="bg-zinc-900/60 border-indigo-500/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <span className="text-xs text-zinc-400">Base Enterprise Value (DCF)</span>
              <div className="font-mono text-2xl font-bold text-indigo-400">{fmt(data.base_ev)}</div>
            </div>
            <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 text-xs">
              &plusmn;{(data.swing_pct * 100).toFixed(0)}% swing
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Tornado Chart */}
      {tornadoData.length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Tornado Chart — EV Impact by Variable</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={tornadoData.length * 55 + 50}>
              <BarChart
                data={tornadoData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 140, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: '#71717a', fontSize: 10 }}
                  tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`}
                />
                <YAxis
                  dataKey="variable"
                  type="category"
                  tick={{ fill: '#a1a1aa', fontSize: 11 }}
                  width={130}
                />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <ReferenceLine x={data?.base_ev || 0} stroke="#6366f1" strokeWidth={2} strokeDasharray="6 3" />
                <Bar dataKey="negativeAbs" stackId="stack" fill="transparent" />
                <Bar dataKey="spread" stackId="stack" radius={[0, 4, 4, 0]}>
                  {tornadoData.map((entry, i) => (
                    <Cell key={i} fill={i % 2 === 0 ? '#6366f1' : '#818cf8'} fillOpacity={0.7 - i * 0.05} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Detailed Table */}
      {tornadoData.length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">Variable Impact Details</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800/60">
                  <th className="text-left text-xs text-zinc-400 font-medium px-4 py-2">Variable</th>
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-2">Low Value</th>
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-2">EV @ Low</th>
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-2">Base EV</th>
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-2">EV @ High</th>
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-2">High Value</th>
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-2">Spread</th>
                </tr>
              </thead>
              <tbody>
                {tornadoData.map((v, i) => (
                  <tr key={i} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                    <td className="text-xs text-zinc-300 font-medium px-4 py-2.5">{v.variable}</td>
                    <td className="text-right font-mono text-xs text-red-400 px-4 py-2.5">{v.lowLabel}</td>
                    <td className="text-right font-mono text-xs text-zinc-200 px-4 py-2.5">{fmt(v.ev_low)}</td>
                    <td className="text-right font-mono text-xs text-zinc-400 px-4 py-2.5">{fmt(v.ev_base)}</td>
                    <td className="text-right font-mono text-xs text-zinc-200 px-4 py-2.5">{fmt(v.ev_high)}</td>
                    <td className="text-right font-mono text-xs text-emerald-400 px-4 py-2.5">{v.highLabel}</td>
                    <td className="text-right px-4 py-2.5">
                      <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 font-mono text-[10px]">
                        {fmt(v.spread)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardContent className="p-4 text-xs text-zinc-400">
          <p><span className="text-zinc-200 font-medium">Interpretation:</span> Variables at the top have the greatest impact on enterprise value. The wider the bar, the more sensitive the valuation is to that assumption. Use this to identify which due diligence items deserve the most attention.</p>
        </CardContent>
      </Card>

      {/* Monte Carlo Section */}
      <div className="border-t border-zinc-800/60 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100 font-['Manrope']">Monte Carlo Simulation</h3>
            <p className="text-sm text-zinc-400 mt-0.5">Probabilistic EV distribution from {nSims.toLocaleString()} random scenarios</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">Simulations:</span>
              <select
                value={nSims}
                onChange={(e) => setNSims(parseInt(e.target.value))}
                className="bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-200 px-2 py-1 focus:outline-none"
                data-testid="mc-sims-select"
              >
                <option value={500}>500</option>
                <option value={1000}>1,000</option>
                <option value={2000}>2,000</option>
                <option value={5000}>5,000</option>
              </select>
            </div>
            <Button
              onClick={runMonteCarlo}
              disabled={mcLoading}
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="run-mc-btn"
            >
              {mcLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Dices className="w-4 h-4 mr-2" />}
              Run Simulation
            </Button>
          </div>
        </div>

        {mcData && (
          <div className="space-y-4">
            {/* Percentile cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {Object.entries(mcData.percentiles).map(([k, v]) => (
                <div key={k} className={`p-2.5 rounded-lg border ${k === 'p50' ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-zinc-800/50 bg-zinc-900/40'}`}>
                  <span className="text-[10px] text-zinc-500 uppercase">{k.replace('p', '')}th</span>
                  <div className={`font-mono text-sm font-semibold ${k === 'p50' ? 'text-indigo-400' : 'text-zinc-200'}`}>{fmt(v)}</div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-zinc-900/60 border-indigo-500/20">
                <CardContent className="p-4">
                  <span className="text-xs text-zinc-400">Mean EV</span>
                  <div className="font-mono text-2xl font-bold text-indigo-400 mt-1">{fmt(mcData.mean)}</div>
                  <span className="text-xs text-zinc-500">&sigma; = {fmt(mcData.std)}</span>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800/60">
                <CardContent className="p-4">
                  <span className="text-xs text-zinc-400">Range (5th - 95th)</span>
                  <div className="font-mono text-lg text-zinc-200 mt-1">{fmt(mcData.percentiles.p5)} — {fmt(mcData.percentiles.p95)}</div>
                  <span className="text-xs text-zinc-500">90% confidence interval</span>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800/60">
                <CardContent className="p-4">
                  <span className="text-xs text-zinc-400">Min / Max</span>
                  <div className="font-mono text-lg text-zinc-200 mt-1">{fmt(mcData.min)} — {fmt(mcData.max)}</div>
                  <span className="text-xs text-zinc-500">{mcData.n_simulations.toLocaleString()} simulations</span>
                </CardContent>
              </Card>
            </div>

            {/* Histogram */}
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-300">EV Distribution (Histogram)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={mcData.histogram}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#71717a', fontSize: 9 }} interval={2} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', fontSize: '11px' }}
                      formatter={(val, name) => [val, 'Occurrences']}
                      labelFormatter={(l) => `Range: ${l}`}
                    />
                    <ReferenceLine x={mcData.histogram.findIndex(h => h.range_low <= mcData.mean && h.range_high > mcData.mean)} stroke="#6366f1" strokeWidth={2} strokeDasharray="6 3" />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {mcData.histogram.map((entry, i) => {
                        const isP25_75 = entry.range_low >= mcData.percentiles.p25 && entry.range_high <= mcData.percentiles.p75;
                        return <Cell key={i} fill={isP25_75 ? '#6366f1' : '#3f3f46'} fillOpacity={isP25_75 ? 0.8 : 0.5} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4 text-xs text-zinc-400">
                <p><span className="text-zinc-200 font-medium">Monte Carlo Method:</span> Each simulation randomly varies discount rate, growth rate, exit multiple, COGS%, and OpEx% using normal distributions around base assumptions. The histogram shows the probability distribution of enterprise values. The 25th-75th percentile range (highlighted) represents the most likely outcomes.</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
